import React, { useState, useEffect } from 'react';
import { Key, CheckCircle, XCircle, Clock, Download, Upload, AlertTriangle } from 'lucide-react';
import { licenseService } from '../services/licenseService';
import { backupService } from '../services/backupService';
import { isFeatureEnabled } from '../config/appConfig';
import toast from 'react-hot-toast';

const LicenseManager: React.FC = () => {
  const [licenseKey, setLicenseKey] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [licenseInfo, setLicenseInfo] = useState<any>(null);
  const [backupFile, setBackupFile] = useState<File | null>(null);

  useEffect(() => {
    loadLicenseInfo();
  }, []);

  const loadLicenseInfo = async () => {
    const info = await licenseService.getLicenseInfo();
    setLicenseInfo(info);
  };

  const handleActivateLicense = async () => {
    if (!licenseKey.trim()) {
      toast.error('Please enter a license key');
      return;
    }

    setIsValidating(true);
    try {
      const result = await licenseService.validateLicense(licenseKey);
      
      if (result.valid) {
        toast.success('License activated successfully');
        setLicenseKey('');
        await loadLicenseInfo();
      } else {
        toast.error(result.error || 'Invalid license key');
      }
    } catch (error) {
      toast.error('Failed to validate license');
    } finally {
      setIsValidating(false);
    }
  };

  const handleClearLicense = async () => {
    if (window.confirm('Are you sure you want to clear the license? This will disable cloud features.')) {
      await licenseService.clearLicense();
      toast.success('License cleared');
      await loadLicenseInfo();
    }
  };

  const handleCreateBackup = async () => {
    try {
      await backupService.createBackup();
    } catch (error) {
      // Error already handled by service
    }
  };

  const handleRestoreBackup = async () => {
    if (!backupFile) {
      toast.error('Please select a backup file');
      return;
    }

    if (window.confirm('This will replace all current data. Are you sure?')) {
      try {
        await backupService.restoreBackup(backupFile);
        toast.success('Backup restored successfully');
        setBackupFile(null);
      } catch (error) {
        // Error already handled by service
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setBackupFile(file);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">License & Backup</h2>

      {/* License Status */}
      <div className="border rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">License Status</h3>
        
        {licenseInfo?.hasLicense ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {licenseInfo.isValid ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <XCircle className="w-5 h-5 text-red-500" />
              )}
              <span className="font-medium">
                {licenseInfo.isValid ? 'Active' : licenseInfo.expiresAt ? 'Expired' : 'Invalid'}
              </span>
            </div>
            
            {licenseInfo.clientName && (
              <div className="text-sm text-gray-600">
                <span className="font-medium">Client:</span> {licenseInfo.clientName}
              </div>
            )}
            
            <div className="text-sm text-gray-600">
              <span className="font-medium">Type:</span> {licenseInfo.type?.toUpperCase()}
            </div>
            
            {licenseInfo.expiresAt && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock className="w-4 h-4" />
                <span>
                  {licenseInfo.daysRemaining !== null && licenseInfo.daysRemaining > 0
                    ? `Expires in ${licenseInfo.daysRemaining} days`
                    : 'Expired'}
                </span>
              </div>
            )}
            
            <button
              onClick={handleClearLicense}
              className="mt-2 text-sm text-red-600 hover:text-red-700"
            >
              Clear License
            </button>
          </div>
        ) : (
          <div className="text-gray-500 text-sm">
            No license activated. Some features may be limited.
          </div>
        )}
      </div>

      {/* License Activation */}
      {!licenseInfo?.hasLicense && (
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Activate License</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                License Key
              </label>
              <input
                type="text"
                value={licenseKey}
                onChange={(e) => setLicenseKey(e.target.value)}
                placeholder="Enter your license key"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              onClick={handleActivateLicense}
              disabled={isValidating}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              <Key className="w-4 h-4" />
              {isValidating ? 'Validating...' : 'Activate License'}
            </button>
          </div>
        </div>
      )}

      {/* Feature Status */}
      <div className="border rounded-lg p-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Available Features</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FeatureItem name="Cloud Sync" enabled={isFeatureEnabled('enableSync')} />
          <FeatureItem name="Auto Sync" enabled={isFeatureEnabled('autoSync')} />
          <FeatureItem name="Multi-Device" enabled={isFeatureEnabled('multiDevice')} />
          <FeatureItem name="Cloud Backup" enabled={isFeatureEnabled('cloudBackup')} />
          <FeatureItem name="Local Backup" enabled={isFeatureEnabled('localBackup')} />
          <FeatureItem name="Local Restore" enabled={isFeatureEnabled('localRestore')} />
          <FeatureItem name="Export Data" enabled={isFeatureEnabled('exportData')} />
          <FeatureItem name="Import Data" enabled={isFeatureEnabled('importData')} />
        </div>
      </div>

      {/* Backup & Restore */}
      {isFeatureEnabled('localBackup') && (
        <div className="border rounded-lg p-4">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Backup & Restore</h3>
          
          <div className="space-y-4">
            <div>
              <button
                onClick={handleCreateBackup}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                <Download className="w-4 h-4" />
                Create Backup
              </button>
              <p className="text-xs text-gray-500 mt-1">
                Creates a backup of all local data
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Restore from Backup
              </label>
              <input
                type="file"
                accept=".json"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
              {backupFile && (
                <button
                  onClick={handleRestoreBackup}
                  className="mt-2 flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                >
                  <Upload className="w-4 h-4" />
                  Restore Backup
                </button>
              )}
              <p className="text-xs text-gray-500 mt-1">
                This will replace all current data
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Offline Mode Notice */}
      {isFeatureEnabled('offlineMode') && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-900">Offline Mode</h4>
              <p className="text-sm text-amber-700 mt-1">
                This device is running in offline mode. All data is stored locally. 
                Use the backup feature regularly to protect your data.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const FeatureItem: React.FC<{ name: string; enabled: boolean }> = ({ name, enabled }) => {
  return (
    <div className="flex items-center gap-2 text-sm">
      {enabled ? (
        <CheckCircle className="w-4 h-4 text-green-500" />
      ) : (
        <XCircle className="w-4 h-4 text-gray-300" />
      )}
      <span className={enabled ? 'text-gray-900' : 'text-gray-400'}>{name}</span>
    </div>
  );
};

export default LicenseManager;
